import * as THREE from "three";
import { motor as M } from "../data/tuning";

/**
 * The opening scene of a run — the original runner's "caught spraying a train" moment, told our way: you steal Elon's
 * car and he wants it back. It plays between the tap and the run (3.6 s; a tap or any key skips it):
 *  1. side view: the roadster parked on a glowing charging pad at the right-hand curb; it unlocks (two blinks) and
 *     rises on its thrusters;
 *  2. the owner's steel pickup rushes in from behind and brakes hard — "HEY! THAT'S MY CAR!" (a DOM bubble);
 *  3. the boosters fire (smoke, sparks), the car pulls out into the middle lane and the camera swings round behind it,
 *     while the pickup lines up at the chaser's gap;
 *  4. it ends on the exact pose the run starts from — car on the start line at the start speed, the chaser at the
 *     snapshot's distance, the chase camera — so the run's first frame follows without a cut.
 * The car does the moving: it is staged behind the start line and accelerates to the start speed by the line, while
 * the world stands still (the road's entities sit relative to the run's distance, which is still 0). No person is
 * drawn: the owner is his truck and his voice.
 */

/** What the scene lends the opening: its own parts, through closures (GameScene.playIntro). */
export interface IntroStage {
  camera: THREE.PerspectiveCamera;
  host: HTMLElement;
  car: THREE.Group;
  chaser: THREE.Group;
  carLength: number;
  chaserLength: number;
  chaserHeight: number;
  hoverGlow?: THREE.Mesh;
  carShadow?: THREE.Mesh;
  /** the chase camera the run starts with (GameScene.updateCamera): eye height, distance behind, look-at offsets */
  chase: { height: number; back: number; lookDown: number; lookAhead: number };
  baseFov(): number;
  body(): THREE.Object3D | undefined;
  jets(down: number, back: number): void;
  wheels(folded: boolean): void;
  chaserWheels(): void;
  spin(metres: number): void;
  burst(x: number, y: number, z: number, color: number, count: number, speed: number, soft?: boolean): void;
  lights(fast: boolean): void;
}

/** Where the run starts (from its snapshot, in scene coordinates): the chaser's centre-to-centre gap, x and height. */
export interface IntroTarget { gap: number; chaserX: number; chaserY: number; }

export interface IntroHooks {
  /** the scene ended on its own after `ms`: start the run */
  done(ms: number): void;
  cue(name: string): void;
}

/** The set: a charging pad, the charger post and the unlock flash. Built once with the scene (so warmUp compiles it). */
export interface IntroSet { root: THREE.Group; pad: THREE.MeshBasicMaterial; flash: THREE.Mesh; }

/** at the right-hand curb (world x is mirrored on screen: −x is the right) — the curb face is at −6.6 */
const PARK_X = -5.3;
/** the pickup pulls up behind, a little out from the curb */
const PICKUP_X = -4.75;
/** metres between the bumpers when he stops, and how far behind that he comes from */
const PICKUP_STOP = 1.5;
const PICKUP_RUN_IN = 26;
const BLINKS = [0.3, 0.56];
const LIFT = [0.78, 1.25] as const;
const ROLL = [0.4, 1.75] as const;
const SHOUT = [1.4, 2.95] as const;
const LAUNCH = 2.1;
/** from the pad to the start line: accelerating to the start speed */
const RUN = 1.5;
export const INTRO_SECONDS = LAUNCH + RUN;
/** the car's height on its thrusters, and the run's hover height (GameScene.updateCar) */
const HOVER = 0.9;
const START_Y = 0.2;
const GREEN = "#14f195";

const lerp = THREE.MathUtils.lerp;
const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));
const smooth = (v: number): number => { const x = clamp01(v); return x * x * (3 - 2 * x); };
const easeInOut = (v: number): number => { const x = clamp01(v); return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2; };
const easeOutBack = (v: number): number => { const x = clamp01(v) - 1; return 1 + 2.4 * x * x * x + 1.4 * x * x; };

function canvasTexture(width: number, height: number, draw: (ctx: CanvasRenderingContext2D) => void): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (ctx) draw(ctx);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
}

function bolt(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number): void {
  ctx.beginPath();
  ctx.moveTo(cx + 0.12 * s, cy - 0.5 * s);
  ctx.lineTo(cx - 0.28 * s, cy + 0.06 * s);
  ctx.lineTo(cx - 0.02 * s, cy + 0.06 * s);
  ctx.lineTo(cx - 0.12 * s, cy + 0.5 * s);
  ctx.lineTo(cx + 0.28 * s, cy - 0.08 * s);
  ctx.lineTo(cx + 0.02 * s, cy - 0.08 * s);
  ctx.closePath();
  ctx.fill();
}

/** The bay on the road: a green outline with corner brackets, hatched ends and a bolt in the middle. */
function padTexture(): THREE.CanvasTexture {
  return canvasTexture(256, 512, (ctx) => {
    ctx.fillStyle = "rgba(20,241,149,0.14)";
    ctx.fillRect(12, 12, 232, 488);
    ctx.strokeStyle = GREEN;
    ctx.lineWidth = 9;
    ctx.strokeRect(16, 16, 224, 480);
    ctx.lineWidth = 16;
    for (const [x, y, dx, dy] of [[8, 8, 1, 1], [248, 8, -1, 1], [8, 504, 1, -1], [248, 504, -1, -1]]) {
      ctx.beginPath();
      ctx.moveTo(x, y + dy * 56); ctx.lineTo(x, y); ctx.lineTo(x + dx * 56, y);
      ctx.stroke();
    }
    ctx.save();
    ctx.beginPath(); ctx.rect(24, 24, 208, 56); ctx.rect(24, 432, 208, 56); ctx.clip();
    ctx.strokeStyle = "rgba(20,241,149,0.55)";
    ctx.lineWidth = 10;
    for (let x = -80; x < 300; x += 30) { ctx.beginPath(); ctx.moveTo(x, 24); ctx.lineTo(x + 70, 488); ctx.stroke(); }
    ctx.restore();
    ctx.fillStyle = "rgba(20,241,149,0.85)";
    bolt(ctx, 128, 256, 170);
  });
}

/** The charger's screen: a bolt, "100%" and a full bar. */
function screenTexture(): THREE.CanvasTexture {
  return canvasTexture(128, 180, (ctx) => {
    ctx.fillStyle = "#06150f";
    ctx.fillRect(0, 0, 128, 180);
    ctx.fillStyle = GREEN;
    bolt(ctx, 64, 50, 66);
    ctx.font = "900 40px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("100%", 64, 128);
    ctx.fillRect(16, 146, 96, 16);
  });
}

export function buildIntroSet(glow: THREE.Texture): IntroSet {
  const root = new THREE.Group();
  root.name = "introSet";
  // the pad: drawn above white so the bloom takes it on high
  const pad = new THREE.MeshBasicMaterial({ map: padTexture(), transparent: true, depthWrite: false });
  pad.color.setScalar(1.6);
  const padMesh = new THREE.Mesh(new THREE.PlaneGeometry(2.9, 5.8), pad);
  padMesh.rotation.x = -Math.PI / 2;
  padMesh.position.y = 0.03;
  padMesh.renderOrder = 1;
  root.add(padMesh);
  // the charger on the sidewalk (top 0.34) just ahead of the nose: a dark post, a green cap, the screen facing the road
  const post = new THREE.Mesh(new THREE.BoxGeometry(0.42, 1.5, 0.34), new THREE.MeshStandardMaterial({ color: 0x2a2c35, roughness: 0.45, metalness: 0.5 }));
  post.position.set(-2.25, 0.34 + 0.75, 3.1);
  post.castShadow = true;
  root.add(post);
  const cap = new THREE.Mesh(new THREE.BoxGeometry(0.46, 0.08, 0.38), new THREE.MeshBasicMaterial({ color: new THREE.Color(GREEN).multiplyScalar(2.2) }));
  cap.position.set(-2.25, 0.34 + 1.54, 3.1);
  root.add(cap);
  const screen = new THREE.MeshBasicMaterial({ map: screenTexture() });
  screen.color.setScalar(1.3);
  const screenMesh = new THREE.Mesh(new THREE.PlaneGeometry(0.32, 0.45), screen);
  screenMesh.rotation.y = Math.PI / 2;                           // faces +x: the road and the camera
  screenMesh.position.set(-2.25 + 0.215, 0.34 + 1.05, 3.1);
  root.add(screenMesh);
  // the unlock blink over the cockpit: a soft violet-white flash, turned to the camera
  const flash = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial({ map: glow, color: 0xe2d4ff, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }));
  flash.scale.set(4.6, 2.8, 1);
  flash.position.set(0, 1.05, -0.2);
  root.add(flash);
  root.visible = false;
  return { root, pad, flash };
}

interface Pose { x: number; y: number; z: number; speed: number; }

export class RunIntro {
  private t = 0;
  private over = false;
  private readonly z0: number;
  private readonly accel: number;
  private readonly freeze: number;
  private readonly parkGap: number;
  private readonly layer: HTMLDivElement;
  private readonly say: HTMLDivElement;
  private readonly eye = new THREE.Vector3();
  private readonly look = new THREE.Vector3();
  private readonly point = new THREE.Vector3();
  private readonly fired = new Set<string>();
  private target: IntroTarget;
  private lastChaserZ = 0;

  constructor(private stage: IntroStage, private set: IntroSet, private hooks: IntroHooks, freezeMs = 0) {
    this.accel = M.startSpeed / RUN;
    this.z0 = -0.5 * M.startSpeed * RUN;                          // it crosses the start line at the start speed
    this.freeze = freezeMs > 0 && freezeMs / 1000 < INTRO_SECONDS ? freezeMs / 1000 : 0;
    // ?freeze is for screenshots, where headless frames are scarce: start half a second before the frozen moment (a freeze
    // after the end: 0.1 s before the end, so the run starts within a few frames)
    if (freezeMs > 0) this.t = freezeMs / 1000 >= INTRO_SECONDS ? INTRO_SECONDS - 0.1 : Math.max(0, freezeMs / 1000 - 0.5);
    this.parkGap = stage.carLength / 2 + stage.chaserLength / 2 + PICKUP_STOP;
    this.target = { gap: this.parkGap + 0.5, chaserX: 0, chaserY: 0 };
    this.lastChaserZ = this.z0 - this.parkGap - PICKUP_RUN_IN;
    set.root.position.set(PARK_X, 0, this.z0);
    set.root.visible = true;
    const touch = typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
    this.layer = document.createElement("div");
    this.layer.className = "intro-layer";
    this.layer.innerHTML = `<i class="intro-bar top" aria-hidden="true"></i><i class="intro-bar bottom" aria-hidden="true"></i>
      <div class="intro-say" hidden><div class="intro-bubble" role="status"><strong>HEY!</strong><span>THAT'S MY CAR!</span></div></div>
      <small class="intro-skip">${touch ? "TAP" : "PRESS ANY KEY"} TO SKIP</small>`;
    stage.host.append(this.layer);
    this.say = this.layer.querySelector<HTMLDivElement>(".intro-say") as HTMLDivElement;
  }

  /** ?freeze: held at that moment (the scene stops its particles too) */
  get frozen(): boolean {
    return this.freeze > 0 && this.t >= this.freeze;
  }

  get finished(): boolean {
    return this.over;
  }

  /** One frame. At its end the scene strikes itself and hands over (hooks.done, after this frame). */
  update(dt: number, target: IntroTarget): void {
    if (this.over) return;
    this.target = target;
    this.t += dt;
    if (this.freeze) this.t = Math.min(this.t, this.freeze);
    const t = Math.min(this.t, INTRO_SECONDS);
    this.pose(t, this.frozen ? 0 : dt);
    if (this.t >= INTRO_SECONDS) {
      this.finish();
      queueMicrotask(() => this.hooks.done(INTRO_SECONDS * 1000));
    }
  }

  /** The last frame (= the run's first): everything where the run expects it, the set struck. Safe to call twice. */
  finish(): void {
    if (this.over) return;
    this.over = true;
    const s = this.stage;
    s.car.position.set(0, START_Y, 0);
    s.car.rotation.set(0, 0, 0);
    const body = s.body();
    if (body) { body.rotation.set(0, 0, 0); body.position.y = 0; body.scale.y = 1; }
    s.jets(0.12, 0.45);
    s.wheels(false);
    if (s.hoverGlow) (s.hoverGlow.material as THREE.MeshBasicMaterial).opacity = 0.28;
    if (s.carShadow) { s.carShadow.position.y = -START_Y + 0.03; s.carShadow.scale.set(1, 1, 1); }
    s.chaser.visible = true;
    s.chaser.position.set(this.target.chaserX, this.target.chaserY, -this.target.gap);
    s.chaser.rotation.set(0, 0, 0);
    this.frame(INTRO_SECONDS, { x: 0, y: START_Y, z: 0, speed: M.startSpeed });
    (this.set.flash.material as THREE.MeshBasicMaterial).opacity = 0;
    this.set.root.visible = false;
    this.layer.remove();
  }

  // ------------------------------------------------------------------------------------------------ the timeline

  private pose(t: number, dt: number): void {
    const s = this.stage;
    const tau = Math.max(0, t - LAUNCH);
    // the car
    const car = this.carAt(t);
    const ahead = this.carAt(t + 0.02);
    const heading = THREE.MathUtils.clamp(Math.atan2(ahead.x - car.x, Math.max(0.05, ahead.z - car.z)), -0.45, 0.45);
    s.car.position.set(car.x, car.y, car.z);
    s.car.rotation.set(0, heading, 0);
    const blink = this.blink(t);
    const lift = clamp01((t - LIFT[0]) / (LIFT[1] - LIFT[0]));
    const body = s.body();
    if (body) {
      // nose up as it rises and as the boosters kick; banked into the pull-out; a dip on its springs at each blink
      const pitch = t < LAUNCH ? -0.05 * Math.sin(lift * Math.PI) : -0.13 * Math.sin(clamp01(tau / 0.9) * Math.PI);
      body.rotation.set(pitch, 0, -heading * 0.7);
      body.position.y = 0;
      body.scale.y = 1 - blink * 0.035;
    }
    const down = t < LIFT[0] ? 0 : t < LAUNCH ? (t < LIFT[1] ? lerp(0.3, 1.05, lift) : 0.8) : lerp(1, 0.12, smooth(tau / RUN));
    const back = t < LIFT[0] ? 0 : t < LAUNCH ? 0.3 : lerp(2.7, 0.45, smooth((tau - 0.25) / (RUN - 0.25)));
    s.jets(down, back);
    if (t >= LAUNCH) s.spin(car.speed * dt);
    if (t >= LIFT[0]) s.wheels(tau < RUN - 0.45);                 // folded on the thrusters, down again for the road
    if (s.hoverGlow) (s.hoverGlow.material as THREE.MeshBasicMaterial).opacity = t < LIFT[0] ? blink * 0.85 : t < LAUNCH ? 0.6 * lift : lerp(0.6, 0.28, smooth(tau / RUN));
    if (s.carShadow) {
      s.carShadow.position.y = -car.y + 0.03;
      const k = Math.max(0.35, 1 - car.y * 0.07);
      s.carShadow.scale.set(k, k, 1);
    }
    const flash = this.set.flash;
    (flash.material as THREE.MeshBasicMaterial).opacity = blink * 0.95;
    flash.quaternion.copy(s.camera.quaternion);
    this.set.pad.opacity = t < LAUNCH ? 0.8 + Math.sin(t * 7) * 0.2 : lerp(1, 0.45, smooth(tau / 0.6));
    // the pickup
    const truck = this.chaserAt(t);
    const truckAhead = this.chaserAt(t + 0.02);
    const truckHeading = THREE.MathUtils.clamp(Math.atan2(truckAhead.x - truck.x, Math.max(0.05, truckAhead.z - truck.z)), -0.4, 0.4);
    const sinceStop = t - ROLL[1];
    const braking = t > ROLL[0] && t < ROLL[1] ? 0.045 * smooth((t - ROLL[0]) / (ROLL[1] - ROLL[0])) : 0;
    const rebound = sinceStop > 0 && t < LAUNCH ? 0.045 * Math.cos(sinceStop * 14) * Math.exp(-sinceStop * 7) : 0;
    s.chaser.visible = t >= ROLL[0] - 0.05;
    s.chaser.position.set(truck.x, truck.y, truck.z);
    s.chaser.rotation.set(braking + rebound, truckHeading, 0);
    if (dt > 0) s.spin(t < LAUNCH ? Math.max(0, truck.z - this.lastChaserZ) : 0);
    this.lastChaserZ = truck.z;
    s.chaserWheels();
    s.lights(t >= SHOUT[0]);
    // one-off moments: the unlock click, dust under the thrusters, his horn, the launch
    this.once("unlock", t >= BLINKS[0], () => this.hooks.cue("bump"));
    this.once("lift", t >= LIFT[0], () => s.burst(car.x, 0.2, car.z, 0xe8e2f2, 16, 2.6, true));
    this.once("shout", t >= SHOUT[0], () => this.hooks.cue("siren"));
    this.once("launch", t >= LAUNCH, () => {
      this.hooks.cue("nitro");
      this.layer.classList.add("launch");
      s.burst(car.x, car.y + 0.3, car.z - 2.4, 0xffffff, 28, 3.6, true);
      s.burst(car.x, car.y + 0.2, car.z - 0.4, 0xffffff, 12, 2.6, true);
      s.burst(car.x, car.y + 0.4, car.z - 2.3, 0xffd27a, 14, 5);
    });
    if (dt > 0 && tau > 0 && tau < 0.9 && Math.random() < 0.55) s.burst(car.x + (Math.random() - 0.5) * 1.2, car.y, car.z - 2.2, 0xffffff, 1, 1.4, true);
    this.frame(t, car);
    this.speak(t, truck);
  }

  /** The roadster: parked (wheels down) → unlocked → up on its thrusters → off the pad, accelerating into the middle lane. */
  private carAt(t: number): Pose {
    const tau = Math.min(RUN, Math.max(0, t - LAUNCH));
    const z = this.z0 + 0.5 * this.accel * tau * tau;
    const x = lerp(PARK_X, 0, smooth((tau - 0.2) / 1.05));
    const up = t < LIFT[0] ? 0 : HOVER * easeOutBack((t - LIFT[0]) / (LIFT[1] - LIFT[0])) + (t > LIFT[1] ? Math.sin((t - LIFT[1]) * 4.2) * 0.05 : 0);
    const y = t < LAUNCH ? up : lerp(up, START_Y, smooth(tau / RUN));
    return { x, y, z, speed: this.accel * tau };
  }

  /** His pickup: rushes in from behind and brakes to a stop; on the launch it follows, falls back a little, closes in. */
  private chaserAt(t: number): Pose {
    const park = this.z0 - this.parkGap;
    if (t < LAUNCH) {
      const left = 1 - clamp01((t - ROLL[0]) / (ROLL[1] - ROLL[0]));    // constant braking: distance left ∝ time left²
      return { x: PICKUP_X, y: 0, z: park - PICKUP_RUN_IN * left * left, speed: 0 };
    }
    const v = Math.min(1, (t - LAUNCH) / RUN);
    const car = this.carAt(t);
    const gap = lerp(this.parkGap, this.target.gap, smooth(v)) + 2.4 * Math.sin(Math.PI * v) ** 2;
    return { x: lerp(PICKUP_X, this.target.chaserX, smooth((v * RUN - 0.25) / 1.05)), y: this.target.chaserY * smooth(v), z: car.z - gap, speed: car.speed };
  }

  /** Two quick blinks: the car unlocking. */
  private blink(t: number): number {
    let v = 0;
    for (const b of BLINKS) if (t >= b && t < b + 0.18) v = Math.max(v, 1 - (t - b) / 0.18);
    return v;
  }

  /**
   * The camera: a low side view of the pad (a 3/4 from the front in portrait, where a side view fits only the car) with a
   * slow push-in, then on the launch an arc round behind the car into the chase camera — the same eye, look-at and FOV
   * GameScene.updateCamera holds at the start, so the hand-over is seamless.
   */
  private frame(t: number, car: Pose): void {
    const s = this.stage, cam = s.camera;
    const portrait = cam.aspect < 0.8;
    const theta0 = portrait ? 2.45 : 1.42;                       // radians round from behind the car (π/2 = its side)
    const r0 = portrait ? 11.5 : 10.4;
    const h0 = portrait ? 2.7 : 1.75;
    const u = easeInOut((t - LAUNCH) / RUN);
    const drift = 1 - smooth(t / LAUNCH);
    const theta = theta0 * (1 - u) + 0.07 * drift;
    const r = lerp(r0 + 1.1 * drift, s.chase.back, u);
    const h = lerp(h0 + 0.2 * drift, s.chase.height, u);
    this.eye.set(car.x + Math.sin(theta) * r, h, car.z - Math.cos(theta) * r);
    cam.position.copy(this.eye);
    const back = portrait ? 3.2 : 3.0;
    this.look.set(
      lerp(car.x, this.eye.x * 0.85, u),
      lerp(0.85, this.eye.y + s.chase.lookDown, u),
      lerp(car.z - back, car.z + s.chase.lookAhead, u));
    cam.lookAt(this.look);
    const fov = lerp(portrait ? 58 : 42, s.baseFov(), u);
    if (cam.fov !== fov) { cam.fov = fov; cam.updateProjectionMatrix(); }   // exact on the last frame: updateCamera takes over from it
  }

  /** The bubble rides above his cab; it points back at him and opens towards the car (flips near the left edge). */
  private speak(t: number, truck: Pose): void {
    const show = t >= SHOUT[0] && t < SHOUT[1] + 0.2;
    if (this.say.hidden === show) this.say.hidden = !show;
    if (!show) return;
    this.say.classList.toggle("out", t >= SHOUT[1]);
    const s = this.stage, cam = s.camera;
    cam.updateMatrixWorld();
    const p = this.point.set(truck.x, truck.y + s.chaserHeight + 0.2, truck.z + 0.5).project(cam);
    const w = s.host.clientWidth, h = s.host.clientHeight;
    if (p.z > 1) { this.say.hidden = true; return; }
    const x = Math.min(w - 12, Math.max(12, ((p.x + 1) / 2) * w));
    const y = Math.min(h - 60, Math.max(150, ((1 - p.y) / 2) * h));
    const bubble = this.say.firstElementChild as HTMLElement | null;
    this.say.classList.toggle("flip", !!bubble && x < bubble.offsetWidth + 24);
    this.say.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`;
  }

  private once(key: string, when: boolean, act: () => void): void {
    if (!when || this.fired.has(key)) return;
    this.fired.add(key);
    act();
  }
}
